import React from 'react';
import { Alert, Card, CardBody, CardHeader, Collapse, Progress } from 'reactstrap';

import { UploadMultipleRef, useAcoBudgetUpload } from '~support-at-home/hooks/useAcoBudgetUpload';

import { Button } from '~components';
import FileUpload from '~components/FileUpload';
import Icon, { faChevronDown, faChevronUp, faCircleCheck, faCircleExclamation, faRefresh } from '~components/icon';
import { Column, TableView } from '~components/table-view';
import WarningDetailsCard from '~support-at-home/components/claims/common/WarningDetailsCard';

import { WarningItem } from '~support-at-home/actions/statements';

import DuplicateReviewTable from './DuplicateReviewTable';

export type { UploadMultipleRef };

interface ComponentProps {
  className?: string;
  onChange?: (field: string, value: UploadMultipleRef) => void;
}

const errorColumns: Column[] = [
  { id: 'line', header: 'Line', type: 'text', style: { width: '60px' } },
  { id: 'reason', header: 'Error Reason', type: 'text' },
];

const budgetWarningColumns: Column[] = [
  { id: 'reason', header: 'Line', type: 'text', style: { width: '60px' } },
  { id: 'details', header: 'Reason', type: 'text' },
];

const listItems = ['CSV format only', 'Maximum file size: 10 MB', 'Must contain valid ACO budget data'];

const UploadMultiple: React.FC<ComponentProps> = ({ className, onChange }) => {
  const {
    wizardState,
    apiError,
    validationErrors,
    isErrorsOpen,
    setIsErrorsOpen,
    warnings,
    loserSelections,
    uploadProgress,
    file,
    detectData,
    isUploading,
    isProcessing,
    isImporting,
    isComplete,
    showUpload,
    isResolving,
    handleFileChange,
    handleUploadAnother,
    handleLoserToggle,
    handleConfirmMerge,
    getTotalLoserCount,
  } = useAcoBudgetUpload({ onChange });

  const renderProgressBar = () => {
    if (isUploading) {
      return <Progress value={uploadProgress} style={{ height: '25px' }} color="success" barAriaLabelledBy="aco-upload-progress-status" />;
    }
    if (isProcessing || isImporting) {
      return <Progress value={100} style={{ height: '25px' }} color="success" animated striped barAriaLabelledBy="aco-upload-progress-status" />;
    }
    return null;
  };

  return (
    <div className={className}>
      {apiError && (
        <Alert color="danger" className="mb-2 p-2 w-100">
          <b>Upload Failed</b>
          <div className="mt-2 mb-0">{apiError}</div>
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Card className="rounded-0 mb-3">
          <CardHeader
            className="d-flex justify-content-between border-0 fw-bold align-items-center"
            type="button"
            onClick={() => setIsErrorsOpen(!isErrorsOpen)}
          >
            <div>
              <Icon icon={faCircleExclamation} className="text-danger me-2" />
              Validation Errors ({validationErrors.length})
            </div>
            <Icon icon={isErrorsOpen ? faChevronUp : faChevronDown} className="ms-2" />
          </CardHeader>
          <Collapse isOpen={isErrorsOpen}>
            <CardBody className="p-0 sah-budget-errors-wrap">
              <TableView
                id="sah-budget-upload-errors"
                columns={errorColumns}
                data={validationErrors}
                emptyMessage="No errors to display"
                readonly
                striped
              />
            </CardBody>
          </Collapse>
        </Card>
      )}

      {warnings.length > 0 && (
        <WarningDetailsCard
          id="aco-budget-upload-warnings"
          title={`${warnings.length} row${warnings.length > 1 ? 's' : ''} skipped`}
          warnings={warnings.map(
            (w): WarningItem => ({
              id: w.id,
              client_name: '',
              reason: `Line ${w.line}`,
              details: w.reason,
            }),
          )}
          columns={budgetWarningColumns}
        />
      )}

      {showUpload && (
        <FileUpload
          allowedExtensions={['.csv']}
          allowedMimeTypes={['text/csv', 'application/vnd.ms-excel']}
          onChange={handleFileChange}
          label="Upload ACO Budget CSV"
          description="Drag & drop CSV file here, or click to browse"
          className="w-100"
          existingFileName={file?.name ?? null}
        />
      )}

      {(isUploading || isProcessing || isImporting) && (
        <div className="mt-3">
          <div className="d-flex justify-content-between mb-1">
            <div id="aco-upload-progress-status" className="text-muted">{isProcessing ? 'Processing…' : 'Uploading…'}</div>
            <div>{isUploading && `${uploadProgress}%`}</div>
          </div>
          {renderProgressBar()}
        </div>
      )}

      {wizardState === 'reviewing' && detectData && (
        <DuplicateReviewTable
          duplicates={detectData.duplicates}
          cleanCount={detectData.clean}
          loserSelections={loserSelections}
          isResolving={isResolving}
          onLoserToggle={handleLoserToggle}
          onConfirm={handleConfirmMerge}
          onCancel={handleUploadAnother}
          totalLoserCount={getTotalLoserCount()}
        />
      )}

      {wizardState === 'resolving' && (
        <div className="mt-3 text-center">
          <Progress value={100} style={{ height: '25px' }} color="primary" animated striped>
            <span className="visually-hidden">Resolving progress</span>
          </Progress>
          <div className="text-muted mt-2">Resolving duplicates and importing budgets…</div>
        </div>
      )}

      {isComplete && (
        <Alert color="success" className="my-2 p-2 w-100">
          <Icon icon={faCircleCheck} className="me-2" />
          <b>File Uploaded Successfully</b>
          <div className="my-2">Your file has been imported. You can upload another.</div>
          <div className="d-flex justify-content-center">
            <Button
              id="sah-client-budgets-add-button"
              size="sm"
              color="success"
              className="align-center"
              onClick={handleUploadAnother}
              data-cy="sah-budget-upload-another-button"
            >
              <Icon className="me-2" icon={faRefresh} />
              Upload Another
            </Button>
          </div>
        </Alert>
      )}

      <div className="criteria-box bg-light p-2 rounded small mt-3">
        <div className="fw-semibold mb-2">File Requirements:</div>
        <ul className="mb-0 ps-3 text-muted">
          {listItems.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default UploadMultiple;
